import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Loading from '@justgiving/loading';
import Modal from '@justgiving/modal';
import Alert from '@justgiving/alert';
import { LessThanMedium, Medium } from '@justgiving/responsive';
import { Field } from 'redux-form';
import { renderField } from 'utils/FormUtils';
import CountrySelect from 'components/FormInputs/CountrySelect';
import { validators } from 'utils';
import StateSelect from 'components/FormInputs/StateSelect';
import FormError from 'components/Alert/FormError';
import AddressSearchResults from 'components/AddressForm/AddressSearchResults';
import { paths } from 'constants';
import { Trans, withTranslation } from 'react-i18next';
import auStates from 'i18n/au-states';
import usStates from 'i18n/us-states';

class AddressFormComponent extends Component {
  static propTypes = {
    fetchData: PropTypes.func.isRequired,
    status: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      fetchError: PropTypes.bool.isRequired,
      postError: PropTypes.bool.isRequired,
      errorMessage: PropTypes.string,
    }).isRequired,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    handleSubmit: PropTypes.func.isRequired,
    submitHandler: PropTypes.func.isRequired,
    resetAppErrors: PropTypes.func.isRequired,
    trackPageView: PropTypes.func.isRequired,
    trackLinkClick: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    submitSucceeded: PropTypes.bool,
    isUpdate: PropTypes.bool,
    findAddressesByPostCode: PropTypes.func.isRequired,
    findAddressResults: PropTypes.shape({
      status: PropTypes.shape({
        isFetching: PropTypes.bool.isRequired,
        error: PropTypes.bool.isRequired,
      }),
    }),
    resetAddressSearchResults: PropTypes.func.isRequired,
    changeFieldValue: PropTypes.func.isRequired,
    postCode: PropTypes.string,
    changeCountry: PropTypes.func.isRequired,
    changeState: PropTypes.func.isRequired,
    hasParams: PropTypes.bool,
    localeData: PropTypes.shape({
      isoCountryCode: PropTypes.string,
      isoStateCode: PropTypes.string,
    }).isRequired,
    findAddressButtonEnabled: PropTypes.bool,
    t: PropTypes.func,
    lng: PropTypes.string,
  };

  static defaultProps = {
    submitting: false,
    submitSucceeded: false,
    error: false,
    isUpdate: false,
    findAddressResults: {},
    postCode: null,
    country: null,
    hasParams: false,
    findAddressButtonEnabled: false,
  };

  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.updateAddressFields = this.updateAddressFields.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.checkValidPostCode = this.checkValidPostCode.bind(this);
    this.updateCountry = this.updateCountry.bind(this);
    this.state = {
      showPostCodeResults: false,
      findPostCodeButtonDisabled: true,
    };
  }

  componentDidMount() {
    const { fetchData, trackPageView, hasParams } = this.props;
    fetchData(!hasParams);
    trackPageView('address', 'personal details > address');
  }

  componentWillReceiveProps(props) {
    const currentPostCode = props.postCode;
    this.checkValidPostCode(currentPostCode);
  }

  componentWillUnmount() {
    this.props.reset();
    this.props.resetAppErrors();
  }

  onSubmit(values) {
    const { trackLinkClick, submitHandler } = this.props;
    trackLinkClick('button', 'personal details > address', 'save');
    return submitHandler(values);
  }

  showAddressResultsModal(PostCode) {
    const {
      findAddressesByPostCode,
      trackLinkClick,
      trackPageView,
    } = this.props;
    findAddressesByPostCode(PostCode);
    trackLinkClick('button', 'personal details > address', 'find');
    trackPageView(
      'address',
      'personal details > address > select your address',
      'select your address',
    );

    this.setState({ showPostCodeResults: true });
  }

  redirectToAddressResultsPage(PostCode) {
    const { findAddressesByPostCode, trackLinkClick } = this.props;
    findAddressesByPostCode(PostCode, paths.AddressResults);
    trackLinkClick('button', 'personal details > address', 'find');
  }

  updateAddressFields(address) {
    this.props.changeFieldValue('AddressLine1', address.AddressLine1);
    this.props.changeFieldValue('AddressLine2', address.AddressLine2);
    this.props.changeFieldValue('Town', address.Town);
    this.props.changeFieldValue('State', address.State);
    this.props.changeFieldValue('Country', address.Country);

    const { resetAddressSearchResults, trackLinkClick } = this.props;
    resetAddressSearchResults();
    trackLinkClick(
      'button',
      'personal details > address > select your address',
      'tick',
    );
    this.setState({ showPostCodeResults: false });
  }

  checkValidPostCode(postCode) {
    this.setState({
      findPostCodeButtonDisabled: !validators.isValidPostCode(postCode),
    });
  }

  closeModal(eventValue) {
    const { resetAddressSearchResults, trackLinkClick } = this.props;
    resetAddressSearchResults();
    trackLinkClick(
      'button',
      'personal details > address > select your address',
      eventValue,
    );
    this.setState({ showPostCodeResults: false });
  }

  updateCountry(val) {
    const { changeCountry, changeFieldValue } = this.props;

    changeCountry(val);
    changeFieldValue('County', '');
  }

  renderAddressForm() {
    const {
      status,
      handleSubmit,
      error, // redux-form error
      submitSucceeded,
      submitting,
      changeState,
      isUpdate,
      findAddressResults,
      postCode,
      localeData,
      t,
      lng,
    } = this.props;

    if (status.isFetching) {
      return <Loading size="large" colour="purple" />;
    }

    const getPostCodeLabel = countryCode => {
      switch (countryCode) {
        case 'GB':
          return <Trans i18nKey="addressForm.postalCode">Postal code</Trans>;
        case 'US':
          return <Trans i18nKey="addressForm.zipCode">ZIP code</Trans>;
        case 'AU':
          return <Trans i18nKey="addressForm.postCode">Postcode</Trans>;
        default:
          return (
            <Trans i18nKey="addressForm.internationalPostCode">
              ZIP/Postal code
            </Trans>
          );
      }
    };

    const zipOrPostCodeLabel = getPostCodeLabel(localeData.isoCountryCode);

    const errorFindingAddressError = (
      <Trans i18nKey="addressForm.errorFindingAddress">
        There was a problem finding your address
      </Trans>
    );

    return (
      <form
        method="POST"
        className="jg-space-mtmd"
        onSubmit={handleSubmit(this.onSubmit)}
        name="addressForm"
      >
        {(status.errorMessage || error) && <FormError error />}
        {findAddressResults.status.error && (
          <FormError comp={errorFindingAddressError} />
        )}
        {submitSucceeded && !status.errorMessage && (
          <Alert
            type="success"
            displayType="inline"
            className="qa-message__success jg-space-mbmd"
          >
            <Trans i18nKey="addressForm.addressUpdated">
              Your address has been updated
            </Trans>
          </Alert>
        )}
        <div className="jg-form-group">
          {isUpdate && (
            <Field
              name="UserAddressId"
              component={renderField}
              type="hidden"
              label="UserAddressId"
            />
          )}

          <div className="jg-form-group form-row">
            <label htmlFor="Country" className="jg-form-label">
              <Trans i18nKey="addressForm.country">Country</Trans>
              <CountrySelect
                disabled={status.fetchError}
                name="CountryCode"
                onChange={(evt, newVal) => this.updateCountry(newVal)}
              />
            </label>
          </div>
          <div className="jg-form-group form-row">
            <label htmlFor="AddressLine1" className="jg-form-label">
              <Trans i18nKey="addressForm.addressline1">Address Line 1</Trans>
              <Field
                name="AddressLine1"
                disabled={status.fetchError}
                component={renderField}
                type="text"
                autoComplete="address-line1"
                label={t(['addressForm.addressline1', 'Address Line 1'])}
              />
            </label>
          </div>
          <div className="jg-form-group form-row">
            <label htmlFor="AddressLine2" className="jg-form-label">
              <Trans i18nKey="addressForm.addressline2">Address Line 2</Trans>
              <Field
                name="AddressLine2"
                disabled={status.fetchError}
                component={renderField}
                type="text"
                autoComplete="address-line2"
                label={t(['addressForm.addressline2', 'Address Line 2'])}
              />
            </label>
          </div>
          <div className="jg-form-group form-row">
            <label htmlFor="Town" className="jg-form-label">
              {localeData.isoCountryCode === 'AU' ? (
                <Trans i18nKey="addressForm.suburb">Suburb</Trans>
              ) : (
                <Trans i18nKey="addressForm.town">Town</Trans>
              )}
              <Field
                name="Town"
                disabled={status.fetchError}
                component={renderField}
                type="text"
                autoComplete="address-level-2"
                maxlength="50"
                label={t(['addressForm.town', 'Town'])}
              />
            </label>
          </div>
          {(localeData.isoCountryCode === 'GB' ||
            localeData.isoCountryCode === 'UK') && (
            <div className="jg-form-group form-row">
              <label htmlFor="County" className="jg-form-label">
                <Trans i18nKey="addressForm.county">County</Trans>
                <Field
                  name="County"
                  disabled={status.fetchError}
                  component={renderField}
                  type="text"
                  autoComplete="address-level-2"
                  maxlength="50"
                  label={t(['addressForm.county', 'County'])}
                />
              </label>
            </div>
          )}
          {(localeData.isoCountryCode === 'US' ||
            localeData.isoCountryCode === 'AU') && (
            <div className="jg-form-group form-row">
              <label htmlFor="County" className="jg-form-label">
                State
                <StateSelect
                  name="County"
                  states={
                    localeData.isoCountryCode === 'US' ? usStates : auStates
                  }
                  onChange={(evt, newVal) => changeState(newVal)}
                />
              </label>
            </div>
          )}
          <div className="row jg-form-group form-row">
            <div className="col-md-7 col-sm-6">
              <label htmlFor="PostCode" className="jg-form-label">
                {zipOrPostCodeLabel}
                <Field
                  name="PostCode"
                  disabled={status.fetchError}
                  component={renderField}
                  type="text"
                  label={zipOrPostCodeLabel}
                  autoComplete="postal-code"
                  onChange={event => {
                    this.checkValidPostCode(event.target.value);
                  }}
                />
              </label>
            </div>
            <div className="col-md-5 col-sm-6">
              {this.props.findAddressButtonEnabled &&
                lng === 'en' &&
                findAddressResults.status.isFetching && (
                  <button
                    disabled={status.fetchError}
                    type="button"
                    className="jg-btn jg-btn--brand qa-button__find-addresses"
                  >
                    <Loading size="small" colour="white" />
                  </button>
                )}
              {this.props.findAddressButtonEnabled &&
                lng === 'en' &&
                !findAddressResults.status.isFetching && (
                  <div>
                    <Medium>
                      <button
                        type="button"
                        className="jg-btn jg-btn--brand qa-button__find-addresses"
                        onClick={() => {
                          this.showAddressResultsModal(postCode);
                        }}
                        disabled={
                          status.fetchError ||
                          this.state.findPostCodeButtonDisabled
                        }
                      >
                        Find
                      </button>
                    </Medium>
                    <LessThanMedium>
                      <button
                        type="button"
                        className="jg-btn jg-btn--brand qa-button__find-addresses"
                        onClick={() => {
                          this.redirectToAddressResultsPage(postCode);
                        }}
                        disabled={
                          status.fetchError ||
                          this.state.findPostCodeButtonDisabled
                        }
                      >
                        Find
                      </button>
                    </LessThanMedium>
                  </div>
                )}
            </div>
          </div>
          <div className="jg-form-group form-row">
            {submitting ? (
              <button type="submit" className="jg-btn jg-btn--brand" disabled>
                <Loading size="small" colour="white" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={status.fetchError}
                className="jg-btn jg-btn--brand qa-button__submit"
              >
                Save
              </button>
            )}
          </div>
        </div>
      </form>
    );
  }

  render() {
    const { findAddressResults } = this.props;
    const { showPostCodeResults } = this.state;
    const addressResults = showPostCodeResults && (
      <AddressSearchResults
        findAddressResults={findAddressResults}
        onSelectAddress={this.updateAddressFields}
      />
    );
    const cancelButton = (
      <div>
        Still can&#39;t find your address?{' '}
        <button
          onClick={() => {
            this.closeModal('fill it in manually');
          }}
          className="qa-button__cancel jg-btn--link"
        >
          Fill it in manually.
        </button>
      </div>
    );
    const addressForm = this.renderAddressForm();

    const displayModal =
      showPostCodeResults &&
      !findAddressResults.status.isFetching &&
      !findAddressResults.status.error;
    return (
      <div>
        <Medium>
          <Modal
            isOpen={displayModal}
            onRequestClose={() => {
              this.closeModal('x');
            }}
            contentLabel="Choose an address"
          >
            {addressResults}
            {cancelButton}
          </Modal>
          {addressForm}
        </Medium>
        <LessThanMedium>{addressForm}</LessThanMedium>
      </div>
    );
  }
}

export default withTranslation()(AddressFormComponent);
